import { ReactNode, useEffect, useRef, useState } from 'react'
import {
  Wrapper,
  Container,
  ImageViewer,
  ContentWrapper,
  Content,
  Footer,
  MissionInfoWrapper,
  Terms,
  ZoomWrapper,
  MissionApprovalWrapper
} from './styles'
import { Box, Image } from '../ui/components'
import { IButtonConfig, SimpleButton } from '../ui/components/buttons/simple'
import { Icon } from '../../assets/icons'
import MissionInfo from '../mission_info'
import MissionApproval from '../mission_approval'
import ZoomModal from '../modais/zoom_modal'
import { useTerms, useGameWrapper, usePage, useMissionTrigger, useGame, useTour } from '../../contexts'
import { customTheme } from '../../styles/theme'
import ImageExamplePng from '../../assets/images/defaults/office.png'
import { getAssetUrl } from '../../utils/assets_helpers'
import { getMissionTimeInSeconds } from '../../utils/mission_helpers'
import { t } from '../../adapters/translate/translate'
import HelpModal from '../modais/help_modal'
import HelpReviewModal from '../modais/help_review_modal'
import ImageGameTest from '../../assets/images/testing-game-bg.png'
import { GameState } from '../../../domain/enum/game-state.enum'
import { isMobile } from '../../utils/general'
import { MissionType } from '../../../domain/enum/mission-type.enum'
import { MissionModel } from '../../../domain/model/mission.model'
import { getLocalStorage } from '../../utils/storage_helpers'

export type IImageConfigProps = {
  source: string;
  size: 'imageFull' | 'imageMedium' | 'imageSmall';
  borderColor: string;
}

type Props = {
  children: ReactNode;
}

const GameWrapper = ({ children }: Props) => {
  const [missionInfoIsShowing, setMissionInfoIsShowing] = useState(true)
  const { handleToggleTermsModal, handleTogglePrivacyPolicyModal } = useTerms()
  const [buttonConfig, setButtonConfig] = useState<IButtonConfig>({} as IButtonConfig)
  const [imageConfig, setImageConfig] = useState<IImageConfigProps>({} as IImageConfigProps)
  const [missionInfoTitle, setMissionInfoTitle] = useState('')
  const [missionInfoDescription, setMissionInfoDescription] = useState('')
  const [openModalReview, setOpenModalReview] = useState(false)

  const {
    activateSubmit, submitButtonIsEnabled,
    currentMission, currentParent,
    currentChildren, doSubmit,
    zoomModalIsOpen, setZoomModalIsOpen,
    handlePreviousMission, setTimePaused, timePaused,
    reviewCompleted, handleReview, loadPreviousMission
  } = useGameWrapper()
  const { toggleHeaderConfig, setHasBackButton } = usePage()
  const [openHelpModal, setOpenHelpModal] = useState<any>(false)
  const [openReviewModal, setOpenReviewModal] = useState<any>(false)
  const [gameInApproval, setGameInApproval] = useState<boolean>(false)
  const [isInitialMissions, setIsInitialMissions] = useState<boolean>(false)
  const [mission, setMission] = useState<MissionModel|null>()
  const { isOpen: missionTriggerIsOpened } = useMissionTrigger()
  const { game, loadPoints } = useGame()
  const { handleToggleTour, tourWasCompleted, showInstructions } = useTour()

  const contentRef = useRef<HTMLDivElement | null>(null)

  function handleSizeTemplate (type: 'imageFull' | 'imageMedium' | 'imageSmall') {
    switch (type) {
      case 'imageFull': return 'large'
      case 'imageMedium': return 'medium'
      case 'imageSmall': return 'small'
      default: return 'medium'
    }
  }

  function handleSubmit () {
    if (gameInApproval) {
      if (submitButtonIsEnabled && reviewCompleted) {
        activateSubmit()
      } else if (!reviewCompleted) {
        setOpenReviewModal(true)
      }
    } else {
      if (submitButtonIsEnabled) {
        activateSubmit()
      }
    }

    setOpenModalReview(false)
  }

  function handleToggleZoomModal () {
    setZoomModalIsOpen((state: any) => !state)
  }

  function loadPageSettings () {
    setMissionInfoIsShowing(true)
  }

  async function loadCurrentGameSettings () {
    if (!currentMission) return
    if (contentRef.current) contentRef.current.scrollTop = 0
    let image: {size: 'imageSmall' | 'imageMedium' | 'imageFull'; source: string } = {
      source: ImageExamplePng,
      size: 'imageMedium'
    }

    if (currentMission.imageFull) {
      image = {
        source: currentMission.imageFull,
        size: 'imageFull'
      }
    } else if (currentMission.imageMedium) {
      image = {
        source: currentMission.imageMedium,
        size: 'imageMedium'
      }
    } else if (currentMission.imageSmall) {
      image = {
        source: currentMission.imageSmall,
        size: 'imageSmall'
      }
    }

    setButtonConfig({
      bg: String(currentMission.buttonColor),
      color: String(currentMission.buttonTextColor),
      text: String(currentMission.buttonText),
      disabled: false
    })
    setImageConfig({
      source: image.source,
      size: image.size,
      borderColor: game?.baseColor || customTheme.colors.purple
    })
    setHasBackButton(currentMission.showBackButton)

    let missionInfoDescriptionTemp = ''
    switch (currentMission.type) {
      case 'picture':
        setMissionInfoTitle(t('PhotoMission'))
        missionInfoDescriptionTemp = `
          <p>1. ${t('PhotoMissionInstructionOne')}</p>
          <p>2. ${t('PhotoMissionInstructionTwo')}</p>
          <p>3. ${t('PhotoMissionInstructionThree')}</p>
        `
        if (!isMobile()) {
          missionInfoDescriptionTemp += `<p>* ${t('PhotoMissionInstructionDesktop')}</p>`
        }
        break
      case 'input_team':
        setMissionInfoTitle(t('TeamName'))
        missionInfoDescriptionTemp = `
          <p>${t('OpenAnswerInstructionOne')}</p>
        `
        break
      case 'input_text':
      case 'input_players':
        setMissionInfoTitle(t('OpenAnswer'))
        missionInfoDescriptionTemp = `
          <p>${t('OpenAnswerInstructionOne')}</p>
        `
        break
      case 'connections':
        setMissionInfoTitle(t('ConnectDots'))
        missionInfoDescriptionTemp = `
          <p>${t('ConnectDotsInstructionOne')}</p>
        `
        break
      case 'simple_text':
        setMissionInfoTitle(t('SimpleText'))
        missionInfoDescriptionTemp = `
          <p>${t('SimpleTextInstructionOne')}</p>
        `
        break
      case 'multiple_choice':
        setMissionInfoTitle(t('MultipleChoice'))
        missionInfoDescriptionTemp = `
          <p>1. ${t('MultipleChoiceInstructionOne')}</p>
          <p>2. ${t('MultipleChoiceInstructionTwo')}</p>
          <p>3. ${t('MultipleChoiceInstructionThree')}.</p>
        `
        break
      case 'input_total_players':
        setMissionInfoTitle(t('MultipleChoice'))
        missionInfoDescriptionTemp = `
          <p>1. ${t('MultipleChoiceInstructionOne')}</p>
          <p>2. ${t('MultipleChoiceInstructionTwo')}</p>
          <p>3. ${t('MultipleChoiceInstructionThree')}.</p>
        `
        break
      default:
        break
    }
    setMissionInfoDescription(missionInfoDescriptionTemp)
    if (game.type === 'linear') {
      await loadPoints()
    }
    toggleHeaderConfig({
      type: currentMission.timeLimitEnabled ? 'timer' : 'blank',
      timeInSeconds: getMissionTimeInSeconds(currentMission.timeLimit)
    })
  }

  const minImage = currentMission?.imageSmall || ''
  const maxImage = currentMission?.imageFull || currentMission?.imageMedium || currentMission?.imageSmall || ''

  useEffect(() => {
    loadPageSettings()
  }, [])

  useEffect(() => {
    loadCurrentGameSettings()
  }, [currentMission, currentParent, currentChildren])

  useEffect(() => {
    if (game.gameState === GameState.APPROVAL) {
      setGameInApproval(true)
      setTimePaused(true)
      handleReview(false)
    }
  }, [missionInfoIsShowing, timePaused])

  useEffect(() => {
    if (!currentMission) return
    setMission(currentMission)
    if (mission?.type === MissionType.INSTRUCTIONS ||
      mission?.type === MissionType.WELCOME ||
      mission?.type === MissionType.INPUT_PLAYERS ||
      mission?.type === MissionType.INPUT_TEAM ||
      mission?.type === MissionType.INPUT_TOTAL_PLAYERS
    ) {
      setIsInitialMissions(true)
    }
  }, [currentMission])

  function buttonEnable () {
    handleReview(true)
  }

  function handlePrevious () {
    setOpenModalReview(false)
    handlePreviousMission()
  }

  useEffect(() => {
    if (game.gameState === GameState.APPROVAL) {
      const alreadyGoToGreetingsPage = getLocalStorage('greetings') || null
      if (!tourWasCompleted && showInstructions && alreadyGoToGreetingsPage) {
        handleToggleTour()
      }
    }
  }, [tourWasCompleted, showInstructions, game])

  return (
    <Wrapper>
      <Container isintestingphase={game.isInTestingPhase} img={ImageGameTest}>
        <ImageViewer bg={imageConfig.borderColor}>
          {imageConfig.source && <Image src={getAssetUrl('image', minImage)} />}

          <ZoomWrapper onClick={handleToggleZoomModal}>
            <Icon name="zoom" />
          </ZoomWrapper>
        </ImageViewer>

        <ContentWrapper>
          <Content gamecolor={game?.baseColor || '#fff'} ref={contentRef}>
            {children}
          </Content>
        </ContentWrapper>

        {gameInApproval && mission && (
          <MissionApprovalWrapper>
            <MissionApproval
              isShowing={missionInfoIsShowing}
              color={game?.baseColor}
              mission={mission}
              onAction={buttonEnable}
              approved={reviewCompleted}
              openModal={openModalReview}
              setOpenModal={setOpenModalReview}
            />
          </MissionApprovalWrapper>
        )}

        {missionInfoTitle &&
          <MissionInfoWrapper>
            { !gameInApproval && <MissionInfo
                  title={missionInfoTitle}
                  description={missionInfoDescription}
                  isShowing={missionInfoIsShowing}
                  color={game?.baseColor}
                />
            }
          </MissionInfoWrapper>}

        <Footer $isOpen={!missionTriggerIsOpened}>

          { gameInApproval && !isInitialMissions && (
            <Box w={'16vw'}>
              <SimpleButton
                onAction={handlePrevious}
                color={'#444'}
                bg={'#f1f1f1'}
                disabled={false}
                isLoading={loadPreviousMission}
              >
                {t('Previous')}
              </SimpleButton>
            </Box>

          )}

          <Box w={'16vw'}>
            <SimpleButton
              onAction={handleSubmit}
              color={buttonConfig.color}
              bg={buttonConfig.bg}
              disabled={!submitButtonIsEnabled}
              isLoading={doSubmit}
            >
              {buttonConfig?.text || 'Enviar'}
            </SimpleButton>
          </Box>
        </Footer>
      </Container>
      <Terms>
        <a style={{ color: game?.textColor || '#fff' }} onClick={handleTogglePrivacyPolicyModal}>{t('PrivacyPolicy')}</a>
        <a style={{ color: game?.textColor || '#fff' }} onClick={handleToggleTermsModal}>{t('Terms of Use')}</a>
        <a style={{ color: game?.textColor || '#fff' }} onClick={() => setOpenHelpModal(true)}>{t('Need help?')}</a>
      </Terms>

      <ZoomModal
        imageURL={getAssetUrl('image', maxImage)}
        isShowing={zoomModalIsOpen}
        handleClose={handleToggleZoomModal}
        type={handleSizeTemplate(imageConfig.size)}
      />
      {openHelpModal && <HelpModal isAnonymous={true} handleClose={() => setOpenHelpModal(false)} />}
      {openReviewModal && <HelpReviewModal isAnonymous={true} handleClose={() => setOpenReviewModal(false)} />}
    </Wrapper>
  )
}

export default GameWrapper
